/**
 * Font size normalization helpers for form line rendering.
 *
 * Form lines store font size as either:
 * - MUI variant keywords: 'h1', 'h2', ..., 'h6', 'subtitle1', 'body1', etc. (V1 editors)
 * - CSS px strings: '18px', '24px', etc. (V2 editors — Smart Forms, Smart Links)
 *
 * The public form renderer (CardFormLine) must handle both formats.
 * These helpers provide a single source of truth for the conversion.
 */

const MUI_VARIANTS = new Set([
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'subtitle1', 'subtitle2',
  'body1', 'body2',
  'caption', 'overline',
]);

/**
 * Returns props to spread onto MUI Typography for the given font size value.
 * Handles both MUI variant keywords and CSS px strings.
 *
 * Usage:
 *   <Typography {...normalizeFontSizeForTypography(textLineSize)}>
 *
 * @param {string} value - e.g. 'h3', '24px', '18px', null
 * @returns {{ variant?: string, sx?: object }}
 */
export const normalizeFontSizeForTypography = (value) => {
  if (!value) return {};
  if (MUI_VARIANTS.has(value)) {
    return { variant: value };
  }
  return { sx: { fontSize: value } };
};

/**
 * Mapping from MUI variant keywords to px values.
 * Used for lazy conversion when opening V1 forms in V2 editors.
 */
const VARIANT_TO_PX = {
  h1: '36px',
  h2: '30px',
  h3: '24px',
  h4: '20px',
  h5: '18px',
  h6: '16px',
  subtitle1: '16px',
  subtitle2: '14px',
  body1: '16px',
  body2: '14px',
  caption: '12px',
  overline: '12px',
};

/**
 * Converts a font size value to px format.
 * V1 variant keywords are mapped to their approximate px equivalent.
 * Existing px values pass through unchanged.
 * Unknown/null defaults to '18px'.
 *
 * @param {string} value - e.g. 'h3', '24px', null
 * @returns {string} - e.g. '24px', '18px'
 */
export const normalizeFontSizeToPx = (value) => {
  if (!value) return '18px';
  if (VARIANT_TO_PX[value]) return VARIANT_TO_PX[value];
  // Already a px or other CSS value — pass through
  return value;
};
